<template>
  <b-container fluid="md">
    <h1 class="mt-2">Salles</h1>

    <div class="my-2 d-flex justify-content-between align-items-center">
      <div class="d-flex">
        <b-form-input
          v-model="search"
          size="sm"
          placeholder="Rechercher..."
        ></b-form-input>

        <b-form-select
          v-model="selectedClient"
          size="sm"
          class="ml-2"
          :options="clientsOptions"
        ></b-form-select>
        <b-form-select
          v-model="affectedRooms"
          size="sm"
          class="ml-2"
          :options="affectedRoomsOptions"
        ></b-form-select>

        <b-button
          :disabled="!isFiltering"
          variant="info"
          size="sm"
          class="ml-2"
          @click="resetFilters()"
          >Annuler</b-button
        >
      </div>
    </div>

    <b-table
      outlined
      :items="list"
      :fields="fields"
      sort-by="client_name"
      primary-key="_id"
      head-variant="light"
    >
      <template #cell(_id)="data">
        <router-link :to="{ name: 'Room', params: { roomId: data.value } }">{{
          data.value
        }}</router-link>
      </template>

      <template #cell(client_id)="data">
        <router-link
          v-if="data.value"
          :to="{ name: 'Client', params: { clientId: data.value } }"
          class="text-nowrap"
          >{{ data.value }}</router-link
        >
        <b-badge v-else variant="danger">Aucun</b-badge>
      </template>

      <template #cell(client_name)="data">
        <router-link
          v-if="data.value"
          :to="{ name: 'Client', params: { clientId: data.item.client._id } }"
          class="text-nowrap"
          >{{ data.value }}</router-link
        >
        <b-badge v-else variant="danger">Aucun</b-badge>
      </template>

      <template #cell(authorized)="data">
        <b-badge v-if="data.value == true" variant="success">Oui</b-badge>
        <b-badge v-else variant="danger">Non</b-badge>
      </template>

      <template #cell(lastStatDate)="data">
        {{ data.value ? formatStatDate(data.value) : '–' }}
      </template>

      <template #cell(lastConnectionDate)="data">
        {{ data.value ? dateFormat(data.value) : '' }}
      </template>

      <template #cell(serverVersion)="data">
        <span :id="'version_tooltip__' + data.item._id">{{
          data.item.metadata
            ? data.item.metadata.serverVersion ||
              data.item.metadata.version_package
            : 'Inconnue'
        }}</span>

        <b-tooltip
          v-if="data.item.metadata && data.item.metadata.version_package"
          :target="'version_tooltip__' + data.item._id"
          placement="top"
          class="text-left"
        >
          <div class="text-left m-0">
            <div class="d-flex justify-content-between">
              <div class="mr-2">Application:</div>
              <div>{{ data.item.metadata.version_package || 'Inconnue' }}</div>
            </div>
            <div class="d-flex justify-content-between">
              <div class="mr-2">Contenu:</div>
              <div>{{ formatDataVersion(data.item.metadata) }}</div>
            </div>
          </div>
        </b-tooltip>
      </template>

      <template #cell(dataVersion)="data">
        <span :id="'package_tooltip__' + data.item._id">{{
          formatDataVersion(data.item.metadata)
        }}</span>
      </template>

      <template #cell(cid)="data">
        <div class="text-center">
          <b-icon
            :icon="data.item.cid ? 'check-circle-fill' : 'x-circle-fill'"
            :variant="data.item.cid ? 'success' : 'danger'"
          ></b-icon>
        </div>
      </template>

      <template #cell(betaAccess)="data">
        <b-badge v-if="data.value == true" variant="warning">Beta</b-badge>
        <b-badge v-else variant="light">Non</b-badge>
      </template>

      <template #cell(other_cids)="data">
        <div class="text-center">
          <span v-if="data.item.other_cids && data.item.other_cids.length > 0">
            <b-icon icon="exclamation-triangle-fill" variant="warning"></b-icon>
            <div>{{ data.item.other_cids.length }}</div>
          </span>
          <span v-else>Inconnue</span>
        </div>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex">
          <router-link
            tag="button"
            class="btn btn-sm btn-info mr-1"
            :to="{ name: 'Logs', params: { roomId: data.item._id } }"
            >Logs</router-link
          >
          <b-button
            v-if="data.item.betaAccess == true"
            size="sm"
            variant="warning"
            @click="updateBetaAccess(data.item, false)"
            >Désactiver Beta mode</b-button
          >
          <b-button
            v-else
            type="button"
            size="sm"
            variant="success"
            @click="updateBetaAccess(data.item, true)"
            >Activer Beta mode</b-button
          >
          <b-button
            size="sm"
            variant="danger"
            class="ml-1"
            @click="deleteRoom(data.index, data.item)"
            >Supprimer</b-button
          >
        </div>
      </template>
    </b-table>
  </b-container>
</template>

<script>
  import axios from 'axios'
  import moment from 'moment'
  import uniqBy from 'lodash/uniqBy'
  import { formatDataVersion } from '@/support/metadata.support'

  export default {
    name: 'Rooms',
    computed: {
      affectedRoomsOptions() {
        return [
          { text: 'Tous', value: null },
          { text: 'Affectées à un client', value: true },
          { text: 'Pas affectées', value: false },
        ]
      },
      clientsOptions() {
        let items = uniqBy(
          this.rooms
            .filter((room) => room.client)
            .map((room) => {
              return { text: room.client.name, value: room.client._id }
            })
            .sort((a, b) => a.text.localeCompare(b.text)),
          'text'
        )

        return [{ text: 'Filtrer par client', value: null }, ...items]
      },
      fields() {
        return [
          {
            key: '_id',
            label: 'ID',
            sortable: true,
            sortDirection: 'asc',
            tdClass: 'text-center font-weight-bold',
          },
          {
            key: 'client_name',
            label: 'Nom Client',
            sortable: true,
            sortDirection: 'asc',
            sortByFormatted: true,
            formatter: (value, key, item) => item.client?.name,
          },
          {
            key: 'authorized',
            label: 'Salle autorisée',
            sortable: true,
            sortDirection: 'asc',
          },
          {
            key: 'lastStatDate',
            label: 'Dernière stat',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'lastConnectionDate',
            label: 'Dernière connexion le',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'serverVersion',
            label: "Version d'application",
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'dataVersion',
            label: 'Version de contenu',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'cid',
            label: 'CID',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'ip',
            label: 'Adresse IP',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'betaAccess',
            label: 'Beta testeur',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'actions',
            label: '',
            tdClass: 'text-center',
          },
          {
            key: 'other_cids',
            label: 'Other CIDs',
            sortable: false,
            tdClass: 'text-center',
          },
        ]
      },
      list() {
        return this.rooms
          .filter((item) => {
            let isPassed = true

            // client
            if (this.selectedClient !== null) {
              isPassed = item.client?._id === this.selectedClient
            }

            return isPassed
          })
          .filter((item) => {
            let isPassed = true

            // affected rooms
            if (this.affectedRooms !== null) {
              if (this.affectedRooms === true) {
                isPassed = !!item.client
              } else {
                isPassed = !item.client
              }
            }

            return isPassed
          })
          .filter((item) => {
            let isPassed = true

            // Keywords
            if (this.search && this.search.trim().length > 0) {
              let keywords = this.search.trim().split(' ')

              isPassed = keywords.every((keyword) => {
                return item._id.toLowerCase().includes(keyword.toLowerCase())
              })

              if (!isPassed && item.client) {
                isPassed = keywords.every((keyword) => {
                  return item.client.name
                    .toLowerCase()
                    .includes(keyword.toLowerCase())
                })
              }

              if (!isPassed && item.client) {
                isPassed = keywords.every((keyword) => {
                  return item.client._id
                    .toLowerCase()
                    .includes(keyword.toLowerCase())
                })
              }

              if (!isPassed && item.serverVersion) {
                isPassed = keywords.every((keyword) => {
                  return item.serverVersion
                    .toLowerCase()
                    .includes(keyword.toLowerCase())
                })
              }
            }

            return isPassed
          })
      },
      isFiltering() {
        return (
          (this.search && this.search.trim().length > 0) ||
          this.affectedRooms !== null ||
          this.selectedClient !== null
        )
      },
    },
    data() {
      return {
        refreshIncrement: 1,
        rooms: [],
        search: '',
        affectedRooms: null,
        selectedClient: null,
      }
    },
    components: {},
    methods: {
      formatDataVersion,
      formatStatDate(date) {
        return moment(date).format('DD/MM/YY HH:mm:ss')
      },
      dateFormat: function (date) {
        return moment(date).local().format('YYYY-MM-DD HH:mm:ss')
      },
      deleteRoom(index, room) {
        let confirm = window.confirm(
          'Êtes-vous sur de vouloir supprimer la salle "' + room._id + '" ?'
        )
        if (confirm === false) {
          return
        }

        const token = localStorage.getItem('jwt')
        if (token) {
          axios
            .delete('/api/room/' + room._id, {
              withCredentials: true,
              headers: {
                Authorization: 'Bearer ' + token,
              },
            })
            .then(() => {
              this.rooms.splice(index, 1)
            })
            .catch(() => {
              this.$router.push('/login')
            })
        } else {
          this.$router.push('/login')
        }
      },
      async updateBetaAccess(room, betaAccess) {
        const token = localStorage.getItem('jwt')
        if (token) {
          let response = await axios.post('/api/room/' + room._id, {
            headers: {
              Authorization: 'Bearer ' + token,
            },
            betaAccess: betaAccess,
          })

          if (response.status === 200) {
            let index = this.rooms.findIndex((item) => item._id === room._id)
            this.rooms[index].betaAccess = betaAccess

            this.$notify({
              type: betaAccess ? 'success' : 'warn',
              title: betaAccess
                ? 'Activation du mode beta'
                : 'Désactivation du mode beta',
            })

            this.refreshIncrement++
            this.$forceUpdate()
          } else {
            this.$notify({
              type: 'error',
              title: 'Erreur',
              text: response.data.message || 'Une erreur est survenue',
            })
          }
        } else {
          this.$router.push('/login')
        }
      },
      resetFilters() {
        this.search = ''
        this.affectedRooms = null
        this.selectedClient = null
      },
    },
    mounted() {
      const token = localStorage.getItem('jwt')
      if (token) {
        axios
          .get('/api/room', {
            withCredentials: true,
            headers: {
              Authorization: 'Bearer ' + token,
            },
          })
          .then((response) => {
            this.rooms = response.data
          })
          .catch(() => {
            this.$router.push('/login')
          })
      } else {
        this.$router.push('/login')
      }
    },
  }
</script>

<style>
  .container {
    margin-top: 2%;
  }

  .thColumn {
    width: 35%;
  }

  .flashy {
    background-color: orange;
    font-weight: bold;
  }
  @keyframes flash {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
</style>
